import { LOOP } from '../constants';

export type UpdateCallback = (dt: number, now: number) => void;
export type RenderCallback = () => void;

/**
 * Variable-dt game loop — one physics step + one render per rAF tick.
 *
 * Why variable, not fixed:
 *   Every motion system here (camera damp, heading damp, speed ramp, chain
 *   follow-the-leader, mass decay) is already frame-rate independent — they
 *   all express change in terms of dt. So feeding them the actual frame dt
 *   produces *perfectly* smooth motion on any monitor refresh rate. A
 *   fixed-step accumulator was tried first; it caused stutter on
 *   non-60-multiple displays because some frames advanced 1 step and others
 *   2.
 *
 * Stability:
 *   - dt is clamped to `MAX_FRAME_DT`. If a frame stalls (tab blur, GC),
 *     we don't fast-forward the whole pause — the game just visibly pauses.
 *   - tiny negative dts (clock-skew) are clipped to 0.
 *
 * stop() is idempotent — safe for React unmount.
 */
export class GameLoop {
  private rafId = 0;
  private lastTime = 0;
  private running = false;

  // FPS sampling — averaged over ~0.5s of real time.
  private fpsAccum = 0;
  private fpsFrames = 0;
  public fps = 0;
  /** Last frame's real dt — used by HUD for diagnostics. */
  public frameDt = 0;

  constructor(
    private readonly onUpdate: UpdateCallback,
    private readonly onRender: RenderCallback,
  ) {}

  start(): void {
    if (this.running) return;
    this.running = true;
    this.lastTime = performance.now();
    this.rafId = requestAnimationFrame(this.frame);
  }

  stop(): void {
    if (!this.running) return;
    this.running = false;
    cancelAnimationFrame(this.rafId);
    this.rafId = 0;
  }

  private frame = (now: number): void => {
    if (!this.running) return;

    let dt = (now - this.lastTime) / 1000;
    this.lastTime = now;
    if (dt < 0) dt = 0;

    // ── Stall handling, three regimes ──────────────────────────────
    //
    // The stutter audit found the previous `if (dt > MAX_FRAME_DT) dt
    // = MAX_FRAME_DT` clamp turned every hiccup ≥100 ms into a visible
    // "snake hangs back behind where it should be" artifact: physics
    // advanced only MAX_FRAME_DT (=100 ms) while wall-clock advanced
    // the full gap, so the snake fell permanently behind. Replacing
    // with regime-aware handling:
    //
    //   - dt > 500 ms  →  TRUE PAUSE (tab hidden / system sleep).
    //                     Discard the gap entirely so we don't simulate
    //                     a 10-second leap in one frame. dt := 1/60.
    //
    //   - 100 ms < dt ≤ 500 ms  →  MODERATE HICCUP (GC pause, fetch
    //                              on focus, asset decode). SUB-STEP:
    //                              run onUpdate multiple times of
    //                              MAX_FRAME_DT each so wall-clock
    //                              catches up smoothly instead of the
    //                              snake hanging back.
    //
    //   - dt ≤ 100 ms  →  NORMAL. One update call as before.
    //
    // Bounded by MAX_SUBSTEPS (=6) so a runaway frame doesn't
    // compound into a death-spiral of catch-up steps.
    const PAUSE_THRESHOLD = 0.5;
    const MAX_SUBSTEPS = 6;
    if (dt > PAUSE_THRESHOLD) {
      dt = 1 / 60;
      this.frameDt = dt;
      this.onUpdate(dt, now);
    } else if (dt > LOOP.MAX_FRAME_DT) {
      const steps = Math.min(Math.ceil(dt / LOOP.MAX_FRAME_DT), MAX_SUBSTEPS);
      const sub = dt / steps;
      this.frameDt = dt;
      for (let i = 0; i < steps; i++) this.onUpdate(sub, now);
    } else {
      this.frameDt = dt;
      this.onUpdate(dt, now);
    }
    this.onRender();

    this.fpsAccum += dt;
    this.fpsFrames++;
    if (this.fpsAccum >= 0.5) {
      this.fps = this.fpsFrames / this.fpsAccum;
      this.fpsAccum = 0;
      this.fpsFrames = 0;
    }

    this.rafId = requestAnimationFrame(this.frame);
  };
}
